import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import invariant from "tiny-invariant";
import { RequestRoomDataParams, RoomEntity } from "@lifeat/shared/data-models/Rooms/RoomEntity";
import { Routes } from "@lifeat/shared/system/RouteConstants";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { functions } from "@firebase-local/firebase";
import { ROOMS_FUNCTIONS } from "@firebase-local/functions/ServerlessEndpoints";
import useOnUserDataLoaded from "@hooks/useOnUserDataLoaded";
import { logError } from "@service/ConsoleLoggerService";
import { BrowserUtil } from "@util/index";
import { generateTwilioRoomAccessToken } from "./RoomService";
import { RoomParams } from "./RoomTypes";
import { VideoChatContextType, useVideoChatContext } from "./VideoChatContext";
import useRoomInteractingBlockedListener from "./useRoomInteractingBlockedListener";
import useRoomMemberInfoListener from "./useRoomMemberInfoListener";

export interface RoomControls {
    readonly roomId: string;
    readonly roomInfo: RoomEntity | undefined;
    readonly setRoomInfo: React.Dispatch<React.SetStateAction<RoomEntity | undefined>>;
    readonly token: string | null;
    readonly isVideoChatInit: boolean;
    readonly numCallParticipants: number;
    readonly setNumCallParticipants: (num: number) => void;
    readonly isRoomScreenSharing: boolean;
    readonly setIsRoomScreenSharing: (share: boolean) => void;
    readonly generateAndJoinCall: () => Promise<void>;
    readonly leaveRoomCall: () => void;
    readonly joinRoom: () => void;
    readonly isInteractingBlocked: boolean;
}

const Context = createContext<RoomControls | undefined>(undefined);

type Props = {
    children: React.ReactNode;
};

export function RoomControllerProvider({ children }: Props) {
    const { roomId }: RoomParams = useParams();
    const { user, userData }: UserContextType = useUserContext();
    const { setIsMyCameraOn, setIsMyMicOn, setIsMyScreenShared }: VideoChatContextType = useVideoChatContext();

    const [roomInfo, setRoomInfo] = useState<RoomEntity | undefined>(undefined);
    const [token, setToken] = useState<string | null>(null); // Twilio tutorial used null here instead of undefined
    const [isVideoChatInit, setIsVideoChatInit] = useState(false);
    const [numCallParticipants, setNumCallParticipants] = useState(1);
    const [isRoomScreenSharing, setIsRoomScreenSharing] = useState(false);

    const isMutedDueToReports = useRoomInteractingBlockedListener(user?.uid, roomId);
    const roomMemberInfo = useRoomMemberInfoListener(user?.uid, roomId);

    const isInteractingBlocked =
        isMutedDueToReports || ((roomMemberInfo?.isMutedByHost || roomMemberInfo?.isBannedByHost) ?? false);

    const isBanned = roomMemberInfo?.isBannedByHost ?? false;
    useEffect(() => {
        // If the user gets banned by the host, they should be redirected to
        // the app (which will take them to their room).
        if (isBanned && roomInfo) {
            // Note: history.push() does not work here - app would not re-render
            // even though the new URL was pushed.
            BrowserUtil.redirectTo(`/${Routes.App}`);
        }
    }, [isBanned, user]);

    // Fetch information for the Room once a user object loads.
    useEffect(() => {
        if (user && roomId && !isBanned) {
            joinRoom();
        }
    }, [roomId, user]);

    function joinRoom() {
        if (user && roomId && !roomInfo) {
            const requestRoomInfoParams: RequestRoomDataParams = {
                roomId: roomId,
            };
            functions
                .httpsCallable(ROOMS_FUNCTIONS.FetchRoomData)(requestRoomInfoParams)
                .then((res) => {
                    const roomInfo: RoomEntity = res.data;
                    setRoomInfo(roomInfo);
                })
                .catch((e) => {
                    logError(String(e));
                });
        }
    }

    // Handler for generating a Twilio access token and joining a Room video call.
    // Backend handles if the requesting user can actually join the call, and only
    // returns a token if it is a valid request.
    const generateAndJoinCall = useCallback(async () => {
        if (user && userData && roomId && !isInteractingBlocked) {
            try {
                const roomToken = await generateTwilioRoomAccessToken(user.uid, userData.email, roomId);
                setIsVideoChatInit(true);
                setToken(roomToken);
            } catch (e) {
                logError(String(e));
            }
        }
    }, [user, userData, roomId, isInteractingBlocked]);

    const handleLeaveRoomCall = useCallback(() => {
        // If the host leaves the Room, close it so that guests are removed
        // and the Video Chat is forced to end.
        setToken(null);

        // Reset context values, so if user starts a new call without refreshing the page,
        // the call won't retain any positively set A/V settings.
        setIsMyCameraOn(false);
        setIsMyMicOn(false);
        setIsMyScreenShared(false);

        if (userData?.roomsData?.personalRoomId === roomId || userData?.roomsData?.roomIds?.includes(roomId)) {
            setIsVideoChatInit(false);
        } else {
            // When host ends the call, everyone is returned to Presence mode.
            setIsVideoChatInit(false);
        }
    }, [token, roomId, userData, isVideoChatInit, roomInfo]);

    const value = {
        roomId: roomId,
        roomInfo: roomInfo,
        setRoomInfo,
        token: token,
        isVideoChatInit: isVideoChatInit,
        numCallParticipants: numCallParticipants,
        setNumCallParticipants: (num: number) => setNumCallParticipants(num),
        isRoomScreenSharing: isRoomScreenSharing,
        setIsRoomScreenSharing: (share: boolean) => setIsRoomScreenSharing(share),
        generateAndJoinCall,
        joinRoom,
        leaveRoomCall: handleLeaveRoomCall,
        isInteractingBlocked: isInteractingBlocked,
    };

    return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useRoomController() {
    const value = useContext(Context);
    invariant(value, "useRoomController must be used within a RoomControllerProvider");
    return value;
}
